import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Play, Plus, Trash } from "@phosphor-icons/react";

export default function Recurring() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", frequency: "monthly",
    start_date: new Date().toISOString().slice(0,10), end_date: "", notes: "",
    lines: [{ name: "", hsn: "", quantity: 1, price: 0, tax_rate: 18 }],
  });

  const load = async () => setRows((await api.get("/recurring")).data);
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  const setLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) });
  const addLine = () => setForm({ ...form, lines: [...form.lines, { name:"", hsn:"", quantity:1, price:0, tax_rate:18 }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.length > 1 ? form.lines.filter((_,idx) => idx !== i) : form.lines });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recurring", { ...form, lines: form.lines.map((l) => ({
        ...l, quantity: Number(l.quantity), price: Number(l.price), tax_rate: Number(l.tax_rate),
      })) });
      toast.success("Recurring schedule created");
      setShow(false);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const runNow = async () => {
    try {
      const { data } = await api.post("/recurring/run");
      toast.success(`Ran: generated ${data.generated} of ${data.checked}`);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 lb-fade" data-testid="recurring-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Automation</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Recurring Invoices</h1>
          <p className="text-neutral-600 text-sm mt-1">Auto-generate invoices on a schedule (subscription/AMC/retainer billing).</p>
        </div>
        <div className="flex gap-2">
          <button className="lb-btn lb-btn-ghost" onClick={runNow} data-testid="run-now">
            <Play size={14} /> Run now
          </button>
          <button className="lb-btn" onClick={() => setShow((s) => !s)} data-testid="add-recurring-btn">
            <Plus size={14} /> New Schedule
          </button>
        </div>
      </div>

      {show && (
        <form onSubmit={submit} className="lb-card p-6 space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <label className="lb-label">Customer</label>
              <select required className="lb-select" value={form.customer_id}
                      onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="rec-customer">
                <option value="">Select customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="lb-label">Frequency</label>
              <select className="lb-select" value={form.frequency}
                      onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="rec-freq">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label className="lb-label">Start Date</label>
              <input type="date" required className="lb-input" value={form.start_date}
                     onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="lb-label">End Date (optional)</label>
              <input type="date" className="lb-input" value={form.end_date}
                     onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="lb-label">Notes</label>
              <input className="lb-input" value={form.notes}
                     onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          <table className="lb-table">
            <thead><tr><th>Item</th><th>HSN/SAC</th><th className="text-right">Qty</th><th className="text-right">Price</th><th className="text-right">GST %</th><th></th></tr></thead>
            <tbody>
              {form.lines.map((l, i) => (
                <tr key={i}>
                  <td><input required className="lb-input" value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} /></td>
                  <td><input className="lb-input font-mono" value={l.hsn} onChange={(e) => setLine(i, { hsn: e.target.value })} /></td>
                  <td><input type="number" step="0.01" className="lb-input text-right font-mono" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                  <td><input type="number" step="0.01" className="lb-input text-right font-mono" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} /></td>
                  <td><input type="number" step="0.01" className="lb-input text-right font-mono" value={l.tax_rate} onChange={(e) => setLine(i, { tax_rate: e.target.value })} /></td>
                  <td><button type="button" onClick={() => removeLine(i)} className="text-neutral-500 hover:text-rose-600"><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={addLine}><Plus size={12} /> Line</button>

          <div className="flex gap-2 pt-3 border-t border-neutral-200">
            <button className="lb-btn">Save Schedule</button>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShow(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="lb-card">
        <table className="lb-table">
          <thead><tr><th>Customer</th><th>Frequency</th><th>Next Run</th><th>Last Run</th><th>Status</th><th>End Date</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-semibold">
                  {customers.find((c) => c.id === r.customer_id)?.name || r.customer_id}
                </td>
                <td><span className="lb-badge">{r.frequency}</span></td>
                <td className="font-mono text-sm">{fmtDate(r.next_run)}</td>
                <td className="font-mono text-sm">{r.last_run ? fmtDate(r.last_run) : "—"}</td>
                <td><span className={"lb-badge " + (r.status === "active" ? "lb-badge-success" : "")}>{r.status}</span></td>
                <td className="font-mono text-sm">{r.end_date ? fmtDate(r.end_date) : "—"}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="text-center py-10 text-neutral-500">No recurring schedules</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
